/* eslint-disable no-unused-vars */
import React, {useEffect, useState} from 'react';
import _flow from 'lodash/flow';
import qs from 'qs';
import Heading from '@nib-components/heading';
import Copy from '@nib-components/copy';
import {Stack, Box, Container, Section} from '@nib/layout';
import metrics from '../metrics';
import {PageDefaultProps} from '../types';
import {BrandContext} from '../services/BrandContext';
import {fetchCustomLinkData, handleCustomLinkCookies} from '../services/CustomLinksService';
import {CustomLinkQueryParameters} from '../types/CustomLinkQueryParameters';
import Layout from '../components/Layout';
import Loader from '@nib/loader';
import {sanitiseQueryParameters} from '../services/utils';
import {isBrowser} from '../utils';

const Custom: React.FC<PageDefaultProps> = ({location, pageContext}) => {
  enum FetchStatus {
    LOADING,
    REDIRECTING,
    AGENT_NOT_FOUND,
    BAD_REQUEST,
    ERROR
  }
  const {brand} = pageContext;
  const siteUrl = process.env.GATSBY_QUOTE_SITE_URL;
  const [status, setStatus] = useState(FetchStatus.LOADING);
  // Parse query parameters for cookie values
  const queryParams: CustomLinkQueryParameters = qs.parse(location.search.substr(1));
  sanitiseQueryParameters(queryParams);

  // Grab eid and sid
  const {sid, eid} = queryParams;

  useEffect(() => {
    (async () => {
      // Pass on query parameters as-is to CustomLink endpoint
      const res = await fetchCustomLinkData(queryParams);

      if (res.status === 200 && res.data) {
        setStatus(FetchStatus.REDIRECTING);

        // Remove old custom links related cookies, set new ones
        await handleCustomLinkCookies(res.data, queryParams, eid, sid);

        const contentSiteParams: Record<string, string> = {
          c: 't' // custom=true
        };

        // Tracking parameters from worker JWT tokens
        if (queryParams.utm_campaign) contentSiteParams.utm_campaign = queryParams.utm_campaign;
        if (queryParams.utm_medium) contentSiteParams.utm_medium = queryParams.utm_medium;
        if (queryParams.utm_source) contentSiteParams.utm_source = queryParams.utm_source;

        // Redirect to content site
        const queryString = qs.stringify(contentSiteParams, {
          addQueryPrefix: true
        });
        if (isBrowser()) {
          window.location.href = `${siteUrl}${queryString}`;
        }
        return;
      }
      // If we can't find agent or if we get validation errors, render Agent Not Found error page
      else if (res.status === 404 || res.status === 400) {
        setStatus(FetchStatus.AGENT_NOT_FOUND);
      } else {
        setStatus(FetchStatus.ERROR);
      }
    })();
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BrandContext.Provider value={brand}>
      <Layout>
        <Container>
          <Section>
            <Box textAlign="center">
              {status === (FetchStatus.LOADING || FetchStatus.REDIRECTING) && <Loader fill={'#444'} />}
              {status === FetchStatus.AGENT_NOT_FOUND && (
                <Stack space={{xs: 6}}>
                  <Heading size={3}>That link does not look right.</Heading>
                  <Copy align="center" large>
                    Please contact your account manager
                  </Copy>
                </Stack>
              )}
              {status === FetchStatus.ERROR && (
                <Stack space={{xs: 6}}>
                  <Heading size={3}>Something went wrong.</Heading>
                  <Copy align="center" large>
                    Please contact your account manager.
                  </Copy>
                </Stack>
              )}
            </Box>
          </Section>
        </Container>
      </Layout>
    </BrandContext.Provider>
  );
};

const enhance = _flow([metrics({pageName: 'Custom'})]);

export default enhance(Custom);
